import { useState, useMemo, useEffect } from 'react'
import { useReadContract, useBalance } from 'wagmi'
import { parseUnits } from 'viem'
import { T } from '@/lib/theme'
import { AmountSlider, Button, MaxLink, Panel, ProgressBar, SectionLabel, ToggleButton, Tooltip } from '@/components/ui'
import { SimulationRows } from '@/components/dashboard/detail/SimulationRows'
import { ADDRESSES } from '@/lib/contracts'
import { ERC20_ABI } from '@/lib/erc20'
import poolAbi from '@/lib/abis/HorizonLendingPool.json'
import gatewayAbi from '@/lib/abis/WETHGateway.json'
import { fmt, fmtBalance, fmtBalanceWei, fmtUsd, fmtPct, toNumber, validateAmount, weiToInputString, POLL_SLOW, fmtBps } from '@/lib/utils'
import { useAction } from '@/hooks/useAction'
import { useLiveAccrual } from '@/hooks/useLiveAccrual'
import { useHorizonPriceUsd } from '@/hooks/useHorizonPriceUsd'
import { accountRisk, projectHealthFactor, healthZone } from '@/lib/accountRisk'
import { effectiveBorrowMaxWei, effectiveSupplyMaxWei, capHeadroomWei } from '@/lib/reserveCaps'
import { projectUtilization, ratesAtUtilization } from '@/lib/rateCurve'

const MAX_UINT = 2n ** 256n - 1n

// ─────────────────────────────────────────────────────────────────
// The reserve action forms, extracted from MarketsSection.jsx so /markets and /markets/:asset run
// ONE implementation rather than two that drift. PRINCIPLES.md's one-implementation rule, and the
// specific scar behind it: this app already shipped two icon resolvers that disagreed about WBTC
// and wstETH, because the second one was a copy nobody remembered to extend.
//
// The forms gained `before -> after` simulation rows in the same move (see SimulationRows.jsx for
// why the transition format beats a conditional warning).
// ─────────────────────────────────────────────────────────────────

// Zone -> color/label - shared by the health-factor bar and the pre-submit borrow warning so
// "caution" and "danger" always mean the same threshold and read the same way everywhere.
export const ZONE_META = {
  safe:    { color: T.positive, label: 'Healthy' },
  caution: { color: T.warning,  label: 'Caution' },
  danger:  { color: T.negative, label: 'Danger' },
}

/// Distance-to-liquidation, shown wherever a user is about to take on or already carries debt.
export function DropToLiquidation({ pct, symbol }) {
  if (pct == null) return null
  // Under 10% is close enough that an ordinary day gets there - coloured as a warning rather than
  // left as a neutral statistic the reader has to interpret.
  const color = pct < 5 ? T.negative : pct < 15 ? T.warning : T.textMuted
  return (
    <div style={{ marginTop: 10, color, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
      {pct <= 0
        ? `This position is already liquidatable.`
        : `Liquidated if ${symbol} falls about ${pct.toFixed(1)}% from here.`}
    </div>
  )
}

export function LiquidationWarning({ zone }) {
  if (zone === 'safe') return null
  const { color, label } = ZONE_META[zone]
  const message = zone === 'danger'
    ? 'This amount uses nearly all of your available borrowing power - a small price move could trigger liquidation.'
    : 'This amount uses most of your available borrowing power - keep an eye on your position.'
  return (
    <div style={{
      marginTop: 12, padding: '10px 14px', borderRadius: T.r.md,
      border: `1px solid ${color}`, background: zone === 'danger' ? T.burnDim : T.warningDim,
      display: 'flex', gap: 10, alignItems: 'flex-start',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, marginTop: 5, flexShrink: 0 }} />
      <div>
        <div style={{ color, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {label} zone (approx.)
        </div>
        <div style={{ marginTop: 2, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.4 }}>
          {message}
        </div>
      </div>
    </div>
  )
}

// Shown in the Supply/Borrow forms whenever the reserve actually has a cap set (0 means uncapped
// - HorizonLendingPool.addReserve never sets one on its own, see setReserveCaps) - a compact bar
// plus a hover breakdown of exactly what's filling it, so a user can see at a glance whether
// there's still room before attempting a transaction that might revert SupplyCapExceeded/
// BorrowCapExceeded.
export function CapIndicator({ label, filled, capAmount, symbol }) {
  if (!capAmount || capAmount <= 0) return null
  const pct = capAmount > 0 ? Math.min(100, (filled / capAmount) * 100) : 0
  const color = pct >= 95 ? T.negative : pct >= 80 ? T.warning : T.accent
  return (
    <Tooltip
      style={{ display: 'block', width: '100%', marginTop: 12 }}
      content={
        <div style={{ fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.7 }}>
          <div style={{ color: T.text }}>Filled: {fmtPct(pct)}</div>
          <div style={{ color: T.text }}>Total: {fmt(filled, 2)} {symbol}</div>
          <div style={{ color: T.text }}>Cap: {fmt(capAmount, 2)} {symbol}</div>
        </div>
      }
    >
      <ProgressBar label={`${label} cap`} value={pct} color={color} valueLabel={fmtPct(pct)} style={{ width: '100%', cursor: 'help' }} />
    </Tooltip>
  )
}

// Shown inside a facilitator reserve's own Borrow form (currently just hUSD) - explains how its
// price stays anchored to $1 purely through borrow/repay activity, since it has no supply side
// and no dedicated redemption path (see FacilitatorSupplyNote's own doc comment).
export function PegMechanismNote({ symbol }) {
  return (
    <div style={{
      marginTop: 12, padding: '12px 14px', borderRadius: T.r.md,
      border: `1px solid ${T.border}`, background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        How {symbol} holds its value
      </div>
      <div style={{ marginTop: 6, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6 }}>
        {symbol}'s price stays anchored to $1 through borrowing and repaying activity alone.
        Trading below $1 lets borrowers buy it at a discount and repay their debt at face value,
        pocketing the spread while retiring supply. Trading above $1 lets borrowers mint more
        against spare collateral and sell it at a premium, adding supply. Both directions pull
        price back toward $1.
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────
// Facilitator reserves (currently just hUSD) have no supply side at all - borrowing mints it
// directly against your collateral, repaying burns it, and the contract itself refuses a supply
// attempt outright (FacilitatorNotSupplyable). This replaces the Supply panel with an explanation
// instead of a form nobody can actually submit.
// ─────────────────────────────────────────────────────────────────

export function FacilitatorSupplyNote({ reserve }) {
  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>{reserve.symbol}</SectionLabel>
      <div style={{ marginTop: 12, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.7 }}>
        {reserve.symbol} is minted the moment you borrow it against collateral you've already
        supplied elsewhere, and burned the moment you repay - it's your own collateral's
        liquidity, made spendable.
      </div>
      <div style={{ marginTop: 14, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6 }}>
        Looking to hold or move value in stable dollars? USDx is built for that.{' '}
        {reserve.symbol} is purpose-built for borrowing against your own Horizon position.
      </div>
    </Panel>
  )
}

/// A collateral-only reserve, explained where the Borrow form would otherwise sit.
///
/// Says what the listing IS rather than what it lacks: these shares back loans, and the borrow side
/// is closed because the share's price comes from a time-averaged reading of a pool this protocol
/// does not run. Honest about the tradeoff a depositor is accepting, and shown before a transaction
/// rather than as a revert after one.
export function CollateralOnlyNote({ reserve }) {
  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Collateral only</SectionLabel>
      <div style={{ marginTop: 12, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.body, lineHeight: 1.7 }}>
        {reserve.symbol} is listed as collateral. Supply it and borrow other assets against it, with
        a lower loan-to-value than the blue chips carry and a ceiling on how much the whole protocol
        will lend against it.
      </div>
      <div style={{ marginTop: 14, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6 }}>
        Its price comes from a time-averaged reading of the pool it holds, which is what keeps a
        single trade from moving what your position is worth. Keeping the borrow side closed is part
        of the same choice.
      </div>
    </Panel>
  )
}

// ─────────────────────────────────────────────────────────────────
// Isolation mode, explained BEFORE the transaction rather than discovered as a bare revert.
//
// The contract enforces three rules (contracts/libraries/IsolationLogic.sol) and each one, left
// unexplained, produces a transaction that fails with nothing a user could act on:
//   1. An isolated asset can't share a position with any other collateral, in either direction.
//   2. In isolation mode only allowlisted assets (stablecoins) may be borrowed.
//   3. Each isolated asset has a shared USD debt ceiling, so a borrow can be refused by a limit
//      that has nothing to do with this user's own health factor.
// Rule 3 is the one most worth surfacing unprompted: a healthy account being told "no" by a
// protocol-wide cap is deeply confusing without the number in front of you.
// ─────────────────────────────────────────────────────────────────

export function IsolationWarnings({ reserve, d, user }) {
  if (!reserve) return null

  const otherCollateral = d.reserves.filter((r) => r.key !== reserve.key && r.user?.supplyBalance > 0)
  const usersIsolated = d.reserves.find((r) => r.isIsolated && r.user?.supplyBalance > 0)
  const notes = []

  if (reserve.isIsolated) {
    const used = reserve.isolationDebtUsd
    const ceiling = reserve.isolationCeilingUsd
    const pct = ceiling > 0 ? Math.min(100, (used / ceiling) * 100) : 0
    notes.push({
      tone: T.accent,
      title: `${reserve.symbol} is an isolated market`,
      body: `Debt against it is capped protocol-wide at ${fmtUsd(ceiling)}, of which ${fmtUsd(used)} is drawn (${pct.toFixed(1)}%). The cap is sized to what could realistically be liquidated into this market, so it can refuse a borrow even when your own position is healthy. While ${reserve.symbol} is your collateral you can borrow only stablecoins, and it must stand as your only posted collateral.`,
    })
  }

  // Rule 1, both directions - stated only when this specific wallet would actually hit it.
  if (user && reserve.isIsolated && otherCollateral.length > 0) {
    notes.push({
      tone: T.warning,
      title: `Withdraw your other collateral first`,
      body: `You're supplying ${otherCollateral.map((r) => r.symbol).join(', ')} as collateral. An isolated asset stands alone, so supplying ${reserve.symbol} stays blocked until those are withdrawn.`,
    })
  }
  if (user && !reserve.isIsolated && usersIsolated) {
    notes.push({
      tone: T.warning,
      title: `You're in isolation mode with ${usersIsolated.symbol}`,
      body: `Supplying ${reserve.symbol} as collateral stays blocked while ${usersIsolated.symbol} is posted.${reserve.borrowableInIsolation ? ` You can still borrow ${reserve.symbol} against it.` : ` ${reserve.symbol} is also unavailable to borrow inside isolation mode.`}`,
    })
  }

  if (notes.length === 0) return null
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {notes.map((n) => (
        <div key={n.title} style={{
          padding: '14px 16px', borderRadius: T.r.md,
          border: `1px solid ${n.tone}44`, background: `${n.tone}11`,
        }}>
          <div style={{ color: n.tone, fontFamily: T.fontM, fontSize: T.fs.body, fontWeight: 700 }}>{n.title}</div>
          <div style={{ marginTop: 6, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.6 }}>{n.body}</div>
        </div>
      ))}
    </div>
  )
}

/// Health-factor row shared by both forms. Formats the pool's own convention (2 decimals, and the
/// word rather than a number when there is no debt at all).
function fmtHf(v) {
  if (v == null) return 'No debt'
  return v.toFixed(2)
}

// ─────────────────────────────────────────────────────────────────
// Supply form - deposit/withdraw the selected reserve. The ETH reserve routes through
// WETHGateway (see WETHGateway.sol's own doc comment) so the user still just sends/receives
// native ETH - the pool itself is WETH-only now, no native-ETH concept at all. Supplying ETH
// needs no approval (the gateway wraps `msg.value` itself, same as the old native-pool path
// used to need none); withdrawing ETH needs the aWETH receipt approved to the GATEWAY (not the
// pool) first, since the gateway pulls it via `transferFrom` before burning it - a genuinely new
// approval step the old native-ETH path never needed (native withdraw called the pool directly,
// no intermediary). USDx (or any other plain ERC20 reserve) keeps the same "approve asset to
// pool before first deposit" shape it always used.
// ─────────────────────────────────────────────────────────────────

/// `onProjection` reports the pending amount UP to a parent, so the detail page can mark on its
/// rate curve where this input would move utilisation. The form keeps owning its own amount state
/// - lifting that would have meant threading validation, decimals and the max-withdraw flag out
/// with it, for a chart annotation.
export function SupplyForm({ reserve, d, user, onTx, onProjection }) {
  const action = useAction()
  const isEth = reserve.key === 'eth'
  const [amount, setAmount] = useState('')
  const [mode, setMode] = useState('supply')
  const isSupply = mode === 'supply'
  // Set only by the withdraw "Max" link, cleared on any manual edit - see submit()'s own comment
  // for why this exists instead of just sending the displayed wei amount.
  const [isMaxWithdraw, setIsMaxWithdraw] = useState(false)

  // reserve.decimals, never a hardcoded 18 - see the reserve table in useProtocolState.
  const wei = useMemo(() => { try { return amount && !validateAmount(amount) ? parseUnits(amount, reserve.decimals) : 0n } catch { return 0n } }, [amount, reserve.decimals])

  function setAmountManual(v) { setIsMaxWithdraw(false); setAmount(v) }

  const { data: ethBal, refetch: refetchEthBal } = useBalance({
    address: user, query: { enabled: !!user && isEth && isSupply, refetchInterval: POLL_SLOW },
  })
  const { data: erc20Bal, refetch: refetchErc20Bal } = useReadContract({
    address: reserve.asset, abi: ERC20_ABI, functionName: 'balanceOf', args: user ? [user] : undefined,
    query: { enabled: !!user && !isEth && isSupply, refetchInterval: POLL_SLOW },
  })
  const walletBalWei = isEth ? ethBal?.value : erc20Bal
  // Supply is bounded by the reserve's supply cap too, not just the wallet - so a near-full cap can't
  // offer a supply the pool then reverts (SupplyCapExceeded). See reserveCaps.js.
  const supplyMaxWei = effectiveSupplyMaxWei({
    walletMaxWei: walletBalWei ?? 0n,
    supplyCapWei: reserve.supplyCapWei,
    totalSuppliedWei: reserve.totalSuppliedWei,
  })
  const supplyCapBound = isSupply && (walletBalWei ?? 0n) > supplyMaxWei

  // Approvals are no longer a separate step the user sees. useAction folds any needed approval into
  // the action itself: the underlying asset approved to the POOL for a plain-ERC20 supply, or the
  // aWETH RECEIPT approved to the GATEWAY for an ETH withdraw (withdrawETH pulls it via transferFrom
  // before burning). On a batch-capable wallet that approval rides inside the one confirmation; on a
  // legacy wallet it is the same approve-then-act prompts as before. See INTENT-UX-PLAN.md.
  const postedWei = reserve.user.supplyBalanceWei

  // Supply-side HORIZON incentive - a real, separate stream from this reserve's own interest (its
  // rate is already advertised in the "Supply APY" line below), previously invisible anywhere in
  // the app despite that. Tracked the same way Farm/Stake/Earn track their own pending HORIZON: live-
  // ticked between polls, claimed from Portfolio alongside every other HORIZON reward.
  const { rate: horizonUsd, found: hasHorizonPrice } = useHorizonPriceUsd()
  const userSupplyHorizonRatePerSecond = reserve.totalSupplied > 0
    ? toNumber(reserve.supplyHorizonPerSecond) * (reserve.user.supplyBalance / reserve.totalSupplied)
    : 0
  const liveSupplyHorizon = useLiveAccrual(reserve.user.pendingSupplyHorizon, userSupplyHorizonRatePerSecond)

  // ── Simulation. Supplying grows the liquidity side of utilisation (pushing rates DOWN) and adds
  // borrowing power; withdrawing does the reverse. Both directions run through the same model.
  const typed = toNumber(wei, reserve.decimals)
  const deltaUnderlying = isSupply ? typed : -typed
  const before = accountRisk(d.reserves)
  const after = accountRisk(d.reserves, { [reserve.key]: { supply: deltaUnderlying } })
  const nextUtilBps = projectUtilization(reserve, deltaUnderlying, 'supply')
  const nextRates = ratesAtUtilization(reserve, nextUtilBps)
  const active = wei > 0n
  const horizonAprPct = reserve.supplyHorizonAprBps / 100

  useEffect(() => {
    onProjection?.(active ? { side: 'supply', utilizationBps: nextUtilBps } : null)
  }, [active, nextUtilBps, onProjection])

  return (
    <Panel style={{ padding: 32 }}>
      <SectionLabel>Supply {reserve.symbol}</SectionLabel>
      <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
        <ToggleButton active={isSupply} onClick={() => { setMode('supply'); setAmountManual('') }}>Supply</ToggleButton>
        <ToggleButton active={!isSupply} onClick={() => { setMode('withdraw'); setAmountManual('') }}>Withdraw</ToggleButton>
      </div>
      <input type="text" value={amount} onChange={(e) => setAmountManual(e.target.value)} placeholder="0.00" style={{ ...inputBox, marginTop: 14 }} />
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <span style={{ color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>
          {isSupply ? 'Wallet' : 'Supplied'}: {user ? fmtBalanceWei(isSupply ? walletBalWei : postedWei) : '-'} {reserve.symbol}
          {user && <MaxLink onClick={setMax} />}
        </span>
      </div>
      <AmountSlider
        valueWei={wei} maxWei={isSupply ? supplyMaxWei : postedWei}
        onChangeWei={(w) => setAmountManual(weiToInputString(w, reserve.decimals))} disabled={!user}
      />
      <CapIndicator label="Supply" filled={reserve.totalSupplied} capAmount={reserve.supplyCap} symbol={reserve.symbol} />
      {/* Supply cap is the binding limit (below the wallet balance) - say so, so the capped max reads
          as a market condition. */}
      {supplyCapBound && user && (
        <div style={{ marginTop: 8, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
          {supplyMaxWei === 0n
            ? `${reserve.symbol}'s supply cap is full. No new supply until some is withdrawn or the cap is raised.`
            : `Only ${fmtBalanceWei(supplyMaxWei, reserve.decimals)} ${reserve.symbol} fits under the supply cap right now.`}
        </div>
      )}
      {reserve.supplyHorizonAprBps > 0 && reserve.user.supplyBalance > 0 && (
        <div style={{ marginTop: 8, color: T.positive, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          <span className="tabnum">{fmtBalance(liveSupplyHorizon, 4)}</span>{' HORIZON pending'}
          {hasHorizonPrice ? <> · ≈ <span className="tabnum">{fmtUsd(liveSupplyHorizon * horizonUsd)}</span></> : null}
          {' · claim on Portfolio'}
        </div>
      )}

      <SimulationRows rows={[
        {
          label: 'Supply APY',
          help: 'Interest paid by borrowers, plus HORIZON incentives. Supplying more moves utilisation down, which moves this rate with it.',
          before: reserve.totalSupplyAprBps / 100,
          after: active ? nextRates.supplyRatePct + horizonAprPct : undefined,
          format: (v) => fmtPct(v),
          good: 'up',
        },
        {
          label: 'Your supplied',
          before: reserve.user.supplyBalance,
          after: active ? Math.max(0, reserve.user.supplyBalance + deltaUnderlying) : undefined,
          format: (v) => `${fmt(v, 4)} ${reserve.symbol}`,
          good: 'neutral',
        },
        {
          label: 'Utilisation',
          before: reserve.utilizationBps / 100,
          after: active ? nextUtilBps / 100 : undefined,
          format: (v) => fmtPct(v),
          good: 'neutral',
        },
        d.hasDebt ? {
          label: 'Health factor',
          help: 'Account-wide, spanning every reserve. Liquidation happens at 1.00. Estimated from current oracle prices.',
          before: d.healthFactor != null ? Number(d.healthFactor) / 1e18 : null,
          after: active ? projectHealthFactor(d.healthFactor != null ? Number(d.healthFactor) / 1e18 : null, before, after) : undefined,
          format: fmtHf,
          good: 'up',
        } : null,
        {
          label: 'Available to borrow',
          before: before.availableBorrowsUsd,
          after: active ? after.availableBorrowsUsd : undefined,
          format: (v) => fmtUsd(v),
          good: 'up',
        },
      ]} />

      <div style={{ marginTop: 14 }}>
        {!user ? (
          <Button disabled style={btnFull}>Connect wallet</Button>
        ) : (
          <Button onClick={submit} disabled={!wei || action.pending} style={btnFull}>
            {action.pending ? 'Working…' : isSupply ? 'Supply' : 'Withdraw'}
          </Button>
        )}
      </div>
    </Panel>
  )

  function setMax() {
    if (isSupply) { if (supplyMaxWei > 0n) setAmount(weiToInputString(supplyMaxWei, reserve.decimals)) }
    else if (postedWei > 0n) { setIsMaxWithdraw(true); setAmount(weiToInputString(postedWei, reserve.decimals)) }
  }

  async function submit() {
    let intent
    if (isSupply) {
      // ERC20 supply pulls the asset into the POOL, so it carries that approval; supplyETH wraps
      // msg.value and pulls nothing.
      intent = isEth
        ? { calls: [{ address: ADDRESSES.WETH_GATEWAY, abi: gatewayAbi, functionName: 'supplyETH', args: [], value: wei }], successMsg: `Supplied ${amount} ${reserve.symbol}.` }
        : {
            pulls: [{ token: reserve.asset, spender: ADDRESSES.LENDING_POOL, amount: wei }],
            calls: [{ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'supply', args: [reserve.asset, wei] }],
            // Permit2 path for non-batch wallets: pool.permitPull(isRepay=false, permit, sig).
            permit: { token: reserve.asset, amount: wei, spender: ADDRESSES.LENDING_POOL,
              buildCall: (p, sig) => ({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'permitPull', args: [false, p, sig] }) },
            successMsg: `Supplied ${amount} ${reserve.symbol}.`,
          }
    } else {
      // Max withdraw sends MAX_UINT, not the displayed wei snapshot - postedWei's supply balance
      // keeps accruing interest between the last poll and this tx actually mining, so the
      // snapshot amount would already be stale by execution time, leaving dust behind instead of
      // the full redemption "Max" promised. Both withdraw(MAX_UINT) (pool) and withdrawETH(MAX_UINT)
      // (gateway) resolve the real, current full balance on-chain instead (a security review
      // finding, carried through to the gateway's own identical MAX_UINT handling).
      const withdrawAmount = isMaxWithdraw ? MAX_UINT : wei
      // Only the ETH withdraw pulls a token (the aWETH receipt, into the gateway); the pool withdraw
      // burns the caller's own sToken with no allowance needed. The pull amount is the receipt
      // balance on max, since the call itself sends MAX_UINT.
      intent = isEth
        ? { pulls: [{ token: reserve.sTokenAddress, spender: ADDRESSES.WETH_GATEWAY, amount: isMaxWithdraw ? reserve.user.sTokenBalanceWei : wei }], calls: [{ address: ADDRESSES.WETH_GATEWAY, abi: gatewayAbi, functionName: 'withdrawETH', args: [withdrawAmount] }], successMsg: `Withdrew ${amount} ${reserve.symbol}.` }
        : { calls: [{ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'withdraw', args: [reserve.asset, withdrawAmount] }], successMsg: `Withdrew ${amount} ${reserve.symbol}.` }
    }
    const res = await action.run(intent)
    if (!res) return
    setIsMaxWithdraw(false)
    setAmount('')
    onTx?.()?.catch?.((e) => console.error('refetch after supply tx failed:', e))
    if (isEth && isSupply) refetchEthBal().catch((e) => console.error('refetchEthBal after supply tx failed:', e))
    if (!isEth && isSupply) refetchErc20Bal().catch((e) => console.error('refetchErc20Bal after supply tx failed:', e))
  }
}

// ─────────────────────────────────────────────────────────────────
// Borrow form - borrow/repay the selected reserve. "Available to borrow" for THIS reserve is
// the account-wide USD headroom converted through this reserve's own price (a cross-asset pool
// has no single on-chain "max for this specific reserve" view - headroom is shared across
// whichever reserve you actually draw from), same "approx." caveat this app already uses
// elsewhere for cross-price conversions, with a small safety margin against a live price tick.
// ─────────────────────────────────────────────────────────────────

export function BorrowForm({ reserve, d, user, onTx, onProjection, bare }) {
  const action = useAction()
  const isEth = reserve.key === 'eth'
  const [amount, setAmount] = useState('')
  const [mode, setMode] = useState('borrow')
  const isBorrow = mode === 'borrow'
  // Set only by the repay "Max" link, cleared on any manual edit - see submit()'s own comment
  // for why this exists instead of just sending the displayed wei amount.
  const [isMaxRepay, setIsMaxRepay] = useState(false)

  // reserve.decimals, never a hardcoded 18 - see the reserve table in useProtocolState.
  const wei = useMemo(() => { try { return amount && !validateAmount(amount) ? parseUnits(amount, reserve.decimals) : 0n } catch { return 0n } }, [amount, reserve.decimals])

  function setAmountManual(v) { setIsMaxRepay(false); setAmount(v) }

  // Repay's ERC20 approval (asset -> POOL) is folded into the action by useAction as a `pull`; no
  // separate approve step. repayETH on the gateway just needs msg.value.
  //
  // Borrow-side delegation, ETH reserve only: a pool-level credit delegation to the GATEWAY (mirrors
  // Aave's approveDelegation), NOT an ERC20 allowance - borrowETH reverts InsufficientBorrowAllowance
  // without it, since the gateway (not the caller) calls the pool. Read here so submit() can prepend
  // the one-off delegation call into the borrow intent only when it is short.
  const { data: borrowDelegation } = useReadContract({
    address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'borrowAllowance',
    args: user ? [user, ADDRESSES.WETH_GATEWAY, reserve.asset] : undefined,
    query: { enabled: !!user && isEth && isBorrow, refetchInterval: POLL_SLOW },
  })

  // Only needed to cap the buffered native-ETH msg.value a max-repay sends - see submit()'s own
  // comment.
  const { data: repayEthBal } = useBalance({
    address: user, query: { enabled: !!user && isEth && !isBorrow, refetchInterval: POLL_SLOW },
  })

  const debtWei = reserve.user.debtWei
  // Collateral-driven ceiling: the account's borrowing power converted through this reserve's price.
  const collateralMaxWei = useMemo(() => {
    try {
      const n = reserve.priceUsd > 0 ? (d.accountAvailableBorrowsUsd / reserve.priceUsd) * 0.99 : 0
      return n > 0 ? parseUnits(n.toFixed(reserve.decimals), reserve.decimals) : 0n
    } catch { return 0n }
  }, [d.accountAvailableBorrowsUsd, reserve.priceUsd, reserve.decimals])
  // The REAL max also respects the RESERVE, not just the user's collateral: remaining borrow-cap
  // headroom, the idle (unborrowed) liquidity, and the borrowing-disabled switch (see reserveCaps.js).
  // Without this the UI offered borrows the pool then reverts - e.g. a collateral-only reserve with
  // borrowCap 0 still showed its full collateral amount as borrowable.
  const availableMaxWei = effectiveBorrowMaxWei({
    collateralMaxWei,
    borrowCapWei: reserve.borrowCapWei,
    totalBorrowsWei: reserve.totalBorrowsWei,
    availableLiquidityWei: reserve.availableLiquidityWei,
    borrowingDisabled: reserve.borrowingDisabled,
  })
  // True when the binding limit is the RESERVE (cap / liquidity / disabled), not the user's collateral.
  const reserveBound = collateralMaxWei > availableMaxWei

  // ── Simulation. Borrowing moves BOTH sides of utilisation (debt up, liquidity down) and both
  // sides of health, which is why this runs through the shared model rather than nudging one
  // number - see src/lib/accountRisk.js.
  const typed = toNumber(wei, reserve.decimals)
  const deltaUnderlying = isBorrow ? typed : -typed
  const before = accountRisk(d.reserves)
  const after = accountRisk(d.reserves, { [reserve.key]: { debt: deltaUnderlying } })
  const nextUtilBps = projectUtilization(reserve, deltaUnderlying, 'borrow')
  const nextRates = ratesAtUtilization(reserve, nextUtilBps)
  const active = wei > 0n
  const horizonAprPct = reserve.borrowHorizonAprBps / 100
  const projectedHf = projectHealthFactor(d.healthFactor != null ? Number(d.healthFactor) / 1e18 : null, before, after)
  const projectedZone = isBorrow && active ? healthZone(projectedHf) : 'safe'

  useEffect(() => {
    onProjection?.(active ? { side: 'borrow', utilizationBps: nextUtilBps } : null)
  }, [active, nextUtilBps, onProjection])

  const content = (
    <>
      <SectionLabel>Borrow {reserve.symbol}</SectionLabel>
      <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
        <ToggleButton active={isBorrow} onClick={() => { setMode('borrow'); setAmountManual('') }}>Borrow</ToggleButton>
        <ToggleButton active={!isBorrow} onClick={() => { setMode('repay'); setAmountManual('') }}>Repay</ToggleButton>
      </div>
      {/* Why the button is disabled, spelled out. Without this a borrower with no collateral just
          finds a greyed button and no next step. */}
      {isBorrow && user && d.accountAvailableBorrowsUsd <= 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: T.r.md, border: `1px solid ${T.warning}55`, background: `${T.warning}12`, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
          {d.accountTotalCollateralUsd > 0
            ? "You've reached your borrow limit. Repay some debt or add collateral to borrow more."
            : 'Supply collateral first to unlock borrowing.'}
        </div>
      )}
      {/* You HAVE borrowing power, but the reserve is the binding limit - say which, so a zero/low max
          reads as a market condition rather than a broken button. */}
      {isBorrow && user && d.accountAvailableBorrowsUsd > 0 && reserveBound && (
        <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: T.r.md, border: `1px solid ${T.warning}55`, background: `${T.warning}12`, color: T.textMuted, fontFamily: T.fontM, fontSize: T.fs.caption, lineHeight: 1.5 }}>
          {reserve.borrowingDisabled
            ? `Borrowing ${reserve.symbol} is currently disabled.`
            : capHeadroomWei(reserve.borrowCapWei, reserve.totalBorrowsWei) === 0n
              ? `${reserve.symbol} has reached its borrow cap. No new borrows until some is repaid or the cap is raised.`
              : `Only ${fmtBalanceWei(availableMaxWei, reserve.decimals)} ${reserve.symbol} is available to borrow right now, limited by pool liquidity.`}
        </div>
      )}
      <input type="text" value={amount} onChange={(e) => setAmountManual(e.target.value)} placeholder="0.00" style={{ ...inputBox, marginTop: 14 }} />
      <div style={{ textAlign: 'right', marginTop: 4 }}>
        <span style={{ color: T.textDim, fontSize: T.fs.caption, fontFamily: T.fontM }}>
          {isBorrow ? 'Available to borrow (approx.)' : 'Owed'}: {user ? fmtBalanceWei(isBorrow ? availableMaxWei : debtWei) : '-'} {reserve.symbol}
          {user && <MaxLink onClick={setMax} />}
        </span>
      </div>
      <AmountSlider
        valueWei={wei} maxWei={isBorrow ? availableMaxWei : debtWei}
        onChangeWei={(w) => setAmountManual(weiToInputString(w, reserve.decimals))} disabled={!user}
      />
      {/* utilizationBps isn't meaningful for a facilitator reserve - it has no real liquidity
          to be "utilized", see FacilitatorSupplyNote's own doc comment - so the whole line is
          dropped rather than rendered as an empty row with its own margin. */}
      {!reserve.isFacilitator && (
        <div style={{ marginTop: 8, color: T.textDim, fontFamily: T.fontM, fontSize: T.fs.caption }}>
          {fmtBps(reserve.utilizationBps)} utilization
        </div>
      )}
      <CapIndicator label="Borrow" filled={reserve.totalBorrows} capAmount={reserve.borrowCap} symbol={reserve.symbol} />
      {reserve.isFacilitator && <PegMechanismNote symbol={reserve.symbol} />}

      <SimulationRows rows={[
        {
          label: 'Borrow APR',
          help: 'Interest charged, less HORIZON incentives. Borrowing more moves utilisation up, which moves this rate with it.',
          before: reserve.totalBorrowAprBps / 100,
          after: active ? nextRates.borrowRatePct - horizonAprPct : undefined,
          format: (v) => fmtPct(v),
          good: 'down',
        },
        {
          label: 'Your debt',
          before: reserve.user.debt,
          after: active ? Math.max(0, reserve.user.debt + deltaUnderlying) : undefined,
          format: (v) => `${fmt(v, 4)} ${reserve.symbol}`,
          good: 'down',
        },
        {
          label: 'Health factor',
          help: 'Account-wide, spanning every reserve. Liquidation happens at 1.00. Estimated from current oracle prices.',
          before: d.healthFactor != null && d.hasDebt ? Number(d.healthFactor) / 1e18 : null,
          after: active ? projectedHf : undefined,
          format: fmtHf,
          good: 'up',
        },
        {
          label: 'Liquidated on a fall of',
          help: 'How far your collateral can fall in value before this account may be liquidated.',
          before: before.dropToLiquidationPct,
          after: active ? after.dropToLiquidationPct : undefined,
          format: (v) => (v == null ? 'No debt' : `${v.toFixed(1)}%`),
          good: 'up',
        },
        {
          label: 'Available to borrow',
          before: before.availableBorrowsUsd,
          after: active ? after.availableBorrowsUsd : undefined,
          format: (v) => fmtUsd(v),
          good: 'up',
        },
      ]} />

      <LiquidationWarning zone={projectedZone} />
      <div style={{ marginTop: 14 }}>
        {!user ? (
          <Button disabled style={btnFull}>Connect wallet</Button>
        ) : (
          <Button onClick={submit} disabled={!wei || action.pending} style={btnFull}>
            {action.pending ? 'Working…' : isBorrow ? 'Borrow' : 'Repay'}
          </Button>
        )}
      </div>
    </>
  )

  // A `bare` caller (the hUSD hub) supplies its own panel, so skip the self-wrap to avoid a box-in-box.
  return bare ? content : <Panel style={{ padding: 32 }}>{content}</Panel>

  function setMax() {
    if (isBorrow) setAmount(availableMaxWei > 0n ? weiToInputString(availableMaxWei, reserve.decimals) : '')
    else { setIsMaxRepay(true); setAmount(weiToInputString(debtWei, reserve.decimals)) }
  }

  async function submit() {
    let intent
    if (isBorrow) {
      if (isEth) {
        const calls = []
        // Prepend the credit delegation only when short. It is a pool call, not an ERC20 pull, so
        // it rides in `calls`; on a batch wallet it is invisible, on a legacy wallet it is one
        // prompt before the borrow (same as the old two-step, minus the button dance).
        if ((borrowDelegation ?? 0n) < wei) {
          calls.push({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'approveBorrowDelegation', args: [ADDRESSES.WETH_GATEWAY, reserve.asset, MAX_UINT] })
        }
        calls.push({ address: ADDRESSES.WETH_GATEWAY, abi: gatewayAbi, functionName: 'borrowETH', args: [wei] })
        intent = { calls, successMsg: `Borrowed ${amount} ${reserve.symbol}.` }
      } else {
        intent = { calls: [{ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'borrow', args: [reserve.asset, wei] }], successMsg: `Borrowed ${amount} ${reserve.symbol}.` }
      }
    } else {
      // Max repay sends MAX_UINT, not the displayed wei snapshot - debtWei keeps accruing
      // interest between the last poll and this tx actually mining, so the snapshot amount would
      // already be stale by execution time, leaving dust debt behind instead of the full
      // repayment "Max" promised. Both repay(MAX_UINT) (pool) and repayETH(MAX_UINT) (gateway)
      // resolve the real, current full debt on-chain instead (a security review finding, carried
      // through to the gateway's own identical MAX_UINT handling). The ERC20 approval side approves
      // MAX_UINT unconditionally (useAction's pull), so it's unaffected by this same staleness.
      //
      // The ETH reserve still needs a real msg.value up front - repayETH refunds any unused
      // excess itself (see WETHGateway.sol's own doc comment), so a small buffer over the
      // displayed debt covers the accrual gap instead of risking an undersized send. Capped at
      // the wallet's own balance so the buffer itself can never push the tx over what the user
      // actually holds.
      const repayValue = isMaxRepay && isEth
        ? (() => { const buffered = debtWei + debtWei / 200n; const bal = repayEthBal?.value ?? 0n; return buffered > bal ? bal : buffered })()
        : wei
      // ETH repay carries msg.value and pulls nothing; ERC20 repay pulls the asset into the POOL.
      // For the Permit2 path, max-repay signs a small buffer over the displayed debt (permitPull
      // repays min(received, debt) and refunds the rest), mirroring the ETH buffer above.
      const permitAmount = isMaxRepay ? debtWei + debtWei / 200n : wei
      intent = isEth
        ? { calls: [{ address: ADDRESSES.WETH_GATEWAY, abi: gatewayAbi, functionName: 'repayETH', args: [isMaxRepay ? MAX_UINT : wei], value: repayValue }], successMsg: `Repaid ${amount} ${reserve.symbol}.` }
        : {
            pulls: [{ token: reserve.asset, spender: ADDRESSES.LENDING_POOL, amount: wei }],
            calls: [{ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'repay', args: [reserve.asset, isMaxRepay ? MAX_UINT : wei] }],
            permit: { token: reserve.asset, amount: permitAmount, spender: ADDRESSES.LENDING_POOL,
              buildCall: (p, sig) => ({ address: ADDRESSES.LENDING_POOL, abi: poolAbi, functionName: 'permitPull', args: [true, p, sig] }) },
            successMsg: `Repaid ${amount} ${reserve.symbol}.`,
          }
    }
    const res = await action.run(intent)
    if (!res) return
    setIsMaxRepay(false)
    setAmount('')
    onTx?.()?.catch?.((e) => console.error('refetch after borrow tx failed:', e))
  }
}

// ─────────────────────────────────────────────────────────────────
// Shared styles
// ─────────────────────────────────────────────────────────────────


export const inputBox = {
  width: '100%', background: T.bg2, border: `1px solid ${T.border}`, borderRadius: T.r.md,
  padding: '10px 12px', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.25)',
  color: T.text, fontFamily: T.fontN, fontSize: T.fs.ui, outline: 'none', boxSizing: 'border-box',
}
export const btnFull = { width: '100%', padding: '12px 16px', fontSize: T.fs.body }
